/* iter-53n++ pass 5 — Template outline preview dialog.
 *
 * Fetches /api/me/templates/{tid}/outline and renders the chapter list
 * + character list + short synopsis excerpt so the author can eyeball
 * a template before spinning it up.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, BookOpen, Users, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { templatesApi } from '@/lib/api';

export default function TemplatePreviewDialog({ open, onOpenChange, templateId, onSpawn }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !templateId) return;
    let cancelled = false;
    setLoading(true);
    templatesApi.outline(templateId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load outline.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, templateId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="template-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-primary" />
            {data?.name || 'Template outline'}
            {data?.genre && <Badge variant="outline" className="ml-1 h-5 px-1.5 text-[10px] capitalize">{data.genre}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[12px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading outline…
          </div>
        ) : !data ? null : (
          <div className="space-y-4 py-2">
            {data.synopsis && (
              <section>
                <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Synopsis excerpt</div>
                <p className="max-h-24 overflow-y-auto scroll-thin rounded-md border border-panel-border bg-secondary/20 p-2 text-[12px] leading-relaxed text-foreground/85">{data.synopsis}</p>
              </section>
            )}
            <section>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                <BookOpen className="h-3 w-3" /> Chapters · {data.chapters?.length || 0}
              </div>
              {data.chapters?.length ? (
                <ol className="space-y-1 rounded-md border border-panel-border bg-card p-2">
                  {data.chapters.map((c) => (
                    <li key={c.number} className="flex items-baseline gap-2 text-[12px]" data-testid={`template-preview-ch-${c.number}`}>
                      <span className="font-mono-stat text-[10px] text-muted-foreground w-8 shrink-0">Ch {c.number}</span>
                      <span className="truncate">{c.title}</span>
                    </li>
                  ))}
                </ol>
              ) : <p className="text-[12px] text-muted-foreground">No chapters in this template.</p>}
            </section>
            <section>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Users className="h-3 w-3" /> Characters · {data.characters?.length || 0}
              </div>
              {data.characters?.length ? (
                <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {data.characters.map((c, i) => (
                    <li key={`char-${i}`} className="flex items-baseline gap-2 rounded-md border border-panel-border bg-card px-2 py-1 text-[12px]">
                      <span className="font-medium truncate">{c.name}</span>
                      <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{c.archetype || c.role}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-[12px] text-muted-foreground">No characters in this template.</p>}
            </section>
          </div>
        )}

        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {onSpawn && data && (
            <Button size="sm" onClick={onSpawn} data-testid="template-preview-spawn">
              New from this template
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
